import { MonthlySessions, PeriodKey, PeriodSessions, SessionsFamily, StaffPerformanceRecord } from '../types';

/** The categories in view; `null` means every category, which is also what an empty URL parameter means. */
export type CategorySelection = string[] | null;

export interface ServiceCategoryOption {
  category: string;
  /** Sessions in the latest period across the records the option was collected from. */
  latestSessions: number;
  staffCount: number;
}

export const ALL_CATEGORIES_LABEL = 'All service categories';

/** BigQuery's ROUND(x, 1): half away from zero, never -0. */
const round1 = (value: number): number => {
  const rounded = Math.round((Math.abs(value) + Number.EPSILON) * 10) / 10;
  return value < 0 ? -rounded : rounded;
};

const median = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Period figures the way the view computes them for a category: the mean divides by the months the
 * coach was active in any category, the median is over those months, and active months are the months
 * with a session in the selection itself.
 */
function periodFigures(counts: number[], coachActive: boolean[]): { period: PeriodSessions; rawMean: number | null } {
  const total = counts.reduce((sum, count) => sum + count, 0);
  const coachMonths = coachActive.filter(Boolean).length;
  const rawMean = coachMonths > 0 ? total / coachMonths : null;
  const med = median(counts.filter((_, i) => coachActive[i]));
  return {
    period: {
      total,
      activeMonths: counts.filter((count) => count > 0).length,
      mean: rawMean === null ? null : round1(rawMean),
      median: med === null ? null : round1(med),
    },
    rawMean,
  };
}

/**
 * The sessions family restricted to the selected categories. Every category (`null`) returns the
 * family itself; a document without the breakdown cannot be filtered and reads as no data; a
 * category the coach does not work in contributes zero sessions.
 * @example filterSessionsByCategories(record.sessions, ['Personal Training']) // the view's own PT figures
 */
export function filterSessionsByCategories(sessions: SessionsFamily | null, selected: CategorySelection): SessionsFamily | null {
  if (selected === null) return sessions;
  if (!sessions || sessions.serviceCategories.length === 0) return null;

  const chosen = new Set(selected);
  const picked = sessions.serviceCategories.filter((category) => chosen.has(category.category));
  // The family's own month list keeps the chart's twelve columns whatever the selection.
  const monthly: MonthlySessions[] = sessions.monthly.map((month) => ({
    month: month.month,
    period: month.period,
    count: picked.reduce((sum, category) => sum + (category.monthly.find((m) => m.month === month.month)?.count ?? 0), 0),
  }));

  const figures = (period: PeriodKey) => {
    const indexes = sessions.monthly.map((month, i) => (month.period === period ? i : -1)).filter((i) => i >= 0);
    return periodFigures(
      indexes.map((i) => monthly[i].count),
      indexes.map((i) => sessions.monthly[i].count > 0)
    );
  };
  const latest = figures('latest_6m');
  const prior = figures('prior_6m');
  const comparable =
    latest.rawMean !== null && prior.rawMean !== null && latest.period.total > 0 && prior.period.total > 0 && prior.rawMean > 0;

  return {
    latest: latest.period,
    prior: prior.period,
    meanChange: comparable ? round1(latest.rawMean! - prior.rawMean!) : null,
    meanChangePct: comparable ? round1(((latest.rawMean! - prior.rawMean!) / prior.rawMean!) * 100) : null,
    monthly,
    serviceCategories: sessions.serviceCategories,
  };
}

/** The record with its sessions family filtered; the same object when every category is in view. */
export function applyCategoryFilter(record: StaffPerformanceRecord, selected: CategorySelection): StaffPerformanceRecord {
  if (selected === null) return record;
  return { ...record, sessions: filterSessionsByCategories(record.sessions, selected) };
}

export function applyCategoryFilterToAll(records: StaffPerformanceRecord[], selected: CategorySelection): StaffPerformanceRecord[] {
  return selected === null ? records : records.map((record) => applyCategoryFilter(record, selected));
}

/** Every category any of the records works in, sorted by name, with how much of the latest period it carries. */
export function collectServiceCategories(records: StaffPerformanceRecord[]): ServiceCategoryOption[] {
  const options = new Map<string, ServiceCategoryOption>();
  for (const record of records) {
    for (const category of record.sessions?.serviceCategories ?? []) {
      const option = options.get(category.category) ?? { category: category.category, latestSessions: 0, staffCount: 0 };
      option.latestSessions += category.latest.total;
      option.staffCount += 1;
      options.set(category.category, option);
    }
  }
  return Array.from(options.values()).sort((a, b) => a.category.localeCompare(b.category));
}

/** `?categories=A,B` → ['A', 'B']; blank, missing or only separators → every category. */
export function parseCategoryParam(value: string | null): CategorySelection {
  if (!value) return null;
  const names = value
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  const unique = Array.from(new Set(names));
  return unique.length > 0 ? unique : null;
}

export function serializeCategoryParam(selected: CategorySelection): string | null {
  return selected === null || selected.length === 0 ? null : selected.join(',');
}

/** What the filter control and captions show for a selection. */
export function categoryFilterLabel(selected: CategorySelection): string {
  if (selected === null || selected.length === 0) return ALL_CATEGORIES_LABEL;
  if (selected.length <= 2) return selected.join(', ');
  return `${selected[0]} +${selected.length - 1} more`;
}
